import type { CatalogRatioGroupData, CatalogRatioShortData } from '@gql/graphql';
import { DismantlingCoefficientTypeEnum, RatioTypeEnum } from '@gql/graphql';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { countTrueValues } from 'utils';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGroupsData } from '../CreateOrEditRatioModal/hooks';

import { AddRatiosModalTabs, AddRatiosModalToolbar } from './components';
import { defaultCheckedIdsState, defaultColDefSet } from './constants';
import { defineActiveRatioGroup, getIsRatioGroupItemDisabled, getRowClass, getRowId } from './helpers';
import { useAddRatiosModalColumns, useBaseCostRatioData, useBaseCostRatioOutputData } from './hooks';
import type { TCheckedIds } from './types';

import Styles from './addRatiosModal.module.css';

type TAddCoefficientProps = {
    closeModal: () => void;
    onAdd: (checkedRatioGroups: TCheckedIds) => void;
    precheckedRatios?: CatalogRatioShortData[] | null;
};

const modalTitle = 'Добавление коэффициента к расценке';
const btnConfirmLabel = 'Добавить';
const defaultTabId = RatioTypeEnum.ConstraintCoef;

export const AddRatiosModal = (props: TAddCoefficientProps) => {
    const { closeModal, onAdd, precheckedRatios } = props;

    const [activeTabId, setActiveTabId] = useState<RatioTypeEnum>(defaultTabId);
    const [selectedRatioGroup, setSelectedRatioGroup] = useState<CatalogRatioGroupData | null>(null);
    const [checkedIds, setCheckedIds] = useState<TCheckedIds>({ ...defaultCheckedIdsState });
    const [filters, setFilters] = useState({ ...defaultCheckedIdsState });

    const { groupsData, groupsStatus } = useGroupsData({
        ratioTypeId: activeTabId,
        groupsEffect: !!activeTabId && activeTabId !== RatioTypeEnum.TechPartCoef,
    });

    const groupsItems = useMemo(() => groupsData.map((group) => ({ ...group, label: group.name })), [groupsData]);

    const isConfirmEnabled = useMemo(
        () =>
            Object.keys(checkedIds).reduce((acc, key) => {
                const tabChecked = checkedIds[key as RatioTypeEnum];

                return acc || !!Object.keys(tabChecked).filter((k) => tabChecked[k]).length;
            }, false),
        [checkedIds],
    );

    const countCheckedItems: { [key: string]: number } = useMemo(
        () => R.map(countTrueValues, checkedIds),
        [checkedIds],
    );

    const adaptedPrecheckedIds = useMemo(() => {
        if (precheckedRatios?.length) {
            return precheckedRatios?.filter(Boolean).reduce<TCheckedIds>(
                (acc, item) => {
                    acc[item.ratioType] = {
                        ...acc[item.ratioType],
                        [item.id]: item,
                    };

                    return acc;
                },
                { ...defaultCheckedIdsState } as TCheckedIds,
            );
        }

        return { ...defaultCheckedIdsState };
    }, [precheckedRatios]);

    useEffect(() => {
        setCheckedIds(adaptedPrecheckedIds);
    }, [adaptedPrecheckedIds]);

    const isConstructionWorksGroupSelected = useMemo(
        () =>
            selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks &&
            !selectedRatioGroup?.parentId,
        [selectedRatioGroup?.groupType, selectedRatioGroup?.parentId],
    );
    const isConstructionWorksSubGroupSelected = useMemo(
        () =>
            selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks &&
            !!selectedRatioGroup?.parentId,
        [selectedRatioGroup?.groupType, selectedRatioGroup?.parentId],
    );
    const isEquipmentInstallationWorksGroupSelected = useMemo(
        () =>
            selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation &&
            !selectedRatioGroup?.parentId,
        [selectedRatioGroup?.groupType, selectedRatioGroup?.parentId],
    );

    const {
        searchString,
        handleChangeSearchString,
        data: ratiosData,
        responseRatioType,
        responseGroupId,
        subGroupsNames,
        changeOrderHandler,
        setIsRequestDone: setIsBaseCostRatioDataRequestDone,
    } = useBaseCostRatioData({
        filters,
        ratioType: activeTabId,
        selectedRatioGroup,
        isConstructionWorksGroupSelected,
        groupsStatus,
    });

    const onSelectedRatioGroup = (value: CatalogRatioGroupData | null) => {
        setSelectedRatioGroup(value);
        setIsBaseCostRatioDataRequestDone(false);
    };

    const autoDefinedActiveRatioGroup = useMemo(() => {
        if (selectedRatioGroup) {
            return selectedRatioGroup;
        }

        return defineActiveRatioGroup({
            activeTabId,
            checkedIds,
            groupsItems,
        });
    }, [activeTabId, checkedIds, groupsItems, selectedRatioGroup]);

    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;

            return;
        }
        if (activeTabId !== RatioTypeEnum.TechPartCoef) {
            handleChangeSearchString({ value: null });
        }
    }, [activeTabId, handleChangeSearchString]);

    useEffect(() => {
        if (autoDefinedActiveRatioGroup?.id !== selectedRatioGroup?.id) {
            setSelectedRatioGroup(autoDefinedActiveRatioGroup);
        }
    }, [autoDefinedActiveRatioGroup, selectedRatioGroup?.id]);

    const { currentRowData, updateGroupRowData } = useBaseCostRatioOutputData({
        ratiosData,
        activeTabId,
        responseRatioType,
        selectedRatioGroup,
        responseRatioGroupId: responseGroupId,
    });

    const handleChangeCheckbox = useCallback(
        (row: any) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({
                    ...ids,
                    [activeTabId]: { ...ids[activeTabId], [row.id]: checked ? row : null },
                }));
            },
        [activeTabId],
    );

    const handleAllChangeCheckbox = useCallback(
        (data: Record<string, boolean>) => () => {
            const extendedData = R.keys(data).reduce((acc, ratioId) => {
                if (data[ratioId]) {
                    const ratioDetails = currentRowData?.find((item) => String(item.id) === ratioId) ?? null;

                    return { ...acc, [ratioId]: ratioDetails };
                }

                return acc;
            }, {});

            setCheckedIds((checkedIdsState) => {
                const ratiosToExclude = !R.keys(extendedData).length
                    ? R.keys(checkedIdsState[activeTabId]).reduce((acc, ratioId) => {
                          if (currentRowData?.findIndex((row) => String(row.id) === ratioId) !== -1) {
                              return { ...acc, [ratioId]: null };
                          }

                          return acc;
                      }, {})
                    : {};

                return {
                    ...checkedIdsState,
                    [activeTabId]: {
                        ...(selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks && {
                            ...checkedIdsState[activeTabId],
                            ...ratiosToExclude,
                        }),
                        ...extendedData,
                    },
                };
            });
        },
        [activeTabId, currentRowData, selectedRatioGroup?.groupType],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [activeTabId]: {
                    ...prevFilters[activeTabId],
                    [fieldName]: checkedValues,
                },
            }));
        },
        [activeTabId],
    );

    const handleModalConfirm = useCallback(() => {
        onAdd(checkedIds);
        closeModal();
    }, [checkedIds, closeModal, onAdd]);

    const isSearchDisabled = useMemo(
        () => (activeTabId !== RatioTypeEnum.TechPartCoef ? !responseGroupId || !selectedRatioGroup : false),
        [activeTabId, responseGroupId, selectedRatioGroup],
    );

    const currentTabData = responseRatioType ? ratiosData[responseRatioType] : null;

    const areCheckedRatiosFromCurrentGroup = useMemo(
        () => Boolean(currentRowData?.find((item) => R.keys(checkedIds[activeTabId]).includes(String(item.id)))),
        [activeTabId, checkedIds, currentRowData],
    );

    const canCheckGroupRow = useMemo(() => {
        if (activeTabId === RatioTypeEnum.DismantlingCoef) {
            return !isConstructionWorksGroupSelected;
        }

        if (activeTabId === RatioTypeEnum.ConstraintCoef) {
            if (!R.values(checkedIds[activeTabId])?.filter(Boolean).length) {
                return true;
            }

            return areCheckedRatiosFromCurrentGroup;
        }

        return true;
    }, [activeTabId, areCheckedRatiosFromCurrentGroup, checkedIds, isConstructionWorksGroupSelected]);

    const canCheckRow = useMemo(() => {
        if (activeTabId === RatioTypeEnum.DismantlingCoef) {
            return !isEquipmentInstallationWorksGroupSelected && !isConstructionWorksSubGroupSelected;
        }

        return !(activeTabId === RatioTypeEnum.ConstraintCoef);
    }, [activeTabId, isConstructionWorksSubGroupSelected, isEquipmentInstallationWorksGroupSelected]);

    const tableUniqueKey = useMemo(
        () => `addCoefficients_${activeTabId}${selectedRatioGroup?.id ? `_${selectedRatioGroup?.id}` : ''}`,
        [activeTabId, selectedRatioGroup?.id],
    );

    const { finalColumns } = useAddRatiosModalColumns({
        data: currentTabData,
        checkedIds: checkedIds[activeTabId],
        changeFilterHandler,
        handleChangeCheckbox,
        handleAllChangeCheckbox,
        ratioType: activeTabId,
        ratioGroupId: selectedRatioGroup?.id,
        canCheckGroupRow,
        canCheckRow,
        isConstructionWorksGroupSelected,
        subGroupsNames,
    });

    return (
        <ModalDialog
            title={modalTitle}
            isOpen
            hasOverlay
            onClose={closeModal}
            onConfirm={handleModalConfirm}
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={!isConfirmEnabled}
            btnCancelLabel="Назад"
            withTitleCloseButton
            className="modalDialogAddCoefficient"
            isOnClickOutsideDisable
        >
            <AddRatiosModalTabs
                onChangeActiveTab={(activeTab) => {
                    setSelectedRatioGroup(null);
                    setActiveTabId(activeTab);
                }}
                countItems={countCheckedItems}
                className={Styles.ratioTypesTabs}
                view="bordered"
            />
            <AddRatiosModalToolbar
                ratioGroups={groupsItems}
                selectedRatioType={activeTabId}
                selectedCatalogRatioGroup={selectedRatioGroup}
                onSelectCatalogRatioGroupId={onSelectedRatioGroup}
                isSearchDisabled={isSearchDisabled}
                searchString={searchString}
                handleChangeSearchString={handleChangeSearchString}
                getIsRatioGroupItemDisabled={getIsRatioGroupItemDisabled({
                    ratioGroup: selectedRatioGroup,
                    checkedRatiosByTypeLength: R.values(checkedIds[activeTabId])?.filter(Boolean).length,
                })}
            />
            <div id="addCoefficient" className="ag-theme-alpine AddCoefficient">
                <AgGridTable
                    key={tableUniqueKey}
                    getRowId={getRowId}
                    rowData={updateGroupRowData}
                    columnDefs={finalColumns}
                    rowClassRules={getRowClass}
                    rowSelection="multiple"
                    rowHeight={40}
                    context={{
                        tableUniqueId: tableUniqueKey,
                    }}
                    defaultColDef={defaultColDefSet}
                    overlayNoRowsTemplate={
                        !activeTabId || !selectedRatioGroup?.id
                            ? 'Для отображения данных выберите раздел'
                            : 'Нет данных'
                    }
                    onSortChanged={changeOrderHandler}
                />
            </div>
        </ModalDialog>
    );
};

AddRatiosModal.displayName = 'AddRatiosModal';
